<template>
  <!--
    폴백 — 서버가 `/scope/**` 를 모르는 환경에서는 **종전 셀렉트 그대로**다(§1-6).
    같은 자리에 같은 계약(`storeCd` 하나)이므로 부모 화면은 어느 쪽이 그려졌는지 몰라도 된다.
  -->
  <el-select
    v-if="unavailable && fallback === 'select'"
    class="store-picker store-picker--select"
    :value="value"
    :size="size"
    :disabled="disabled"
    :clearable="clearable"
    :placeholder="placeholderText"
    filterable
    @change="emitPick">
    <!-- 이름을 푸는 규칙은 [storeRowLabel] 한 곳이다 — 창 갈래와 같은 답을 쓴다 -->
    <el-option v-for="row in options" :key="row.storeCd" :label="rowLabel(row)" :value="row.storeCd"></el-option>
  </el-select>

  <!--
    후보 목록이 **없는** 자리(정산 백필 등)의 폴백은 셀렉트가 될 수 없다 — 채울 항목이 없다.
    그 자리들이 종전에 쓰던 **매장 코드 입력**으로 떨어진다: 트리를 모르는 서버에서도 일은 되어야 한다.
    이 갈래에는 `clearable` 이 없다(Element 가 주지 않는다) — 값을 지우면 그것이 빈 값이다.
  -->
  <el-input-number
    v-else-if="unavailable"
    class="store-picker store-picker--number"
    :value="value"
    :size="size"
    :disabled="disabled"
    :placeholder="placeholderText"
    :min="1"
    :controls="false"
    @change="emitPick"></el-input-number>

  <!--
    고를 것이 하나뿐이면 창을 열 이유가 없다 — 이름만 보인다(§1-6). 후보가 **0곳**이면 이 갈래가
    아니라 아래 **버튼**으로 서서 트리에서 고른다(사업장 없는 내부 관리자가 그 경로다).
  -->
  <span v-else-if="fixed" class="store-picker store-picker--fixed" :title="text.onlyStoreHint">{{
    label || placeholderText
  }}</span>

  <span v-else class="store-picker">
    <el-button class="store-picker__field" :size="size" :disabled="disabled" @click="open">
      <span :class="{ 'store-picker__placeholder': !label }">{{ label || placeholderText }}</span>
      <i
        v-if="clearable && value != null && value !== ''"
        class="el-icon-circle-close store-picker__icon"
        @click.stop="clear"></i>
      <i v-else class="el-icon-search store-picker__icon"></i>
    </el-button>

    <StorePickerDialog
      ref="dialog"
      :visible.sync="dialogVisible"
      :multiple="multiple"
      :recent="recent"
      @pick="onPick"
      @confirm="onConfirm"
      @unavailable="onUnavailable" />
  </span>
</template>

<script>
import { mapGetters } from 'vuex'
import StorePickerDialog from './StorePickerDialog.vue'
import { canRead } from '@/plugins/http/permittedRead'
import {
  SCOPE_TREE_ACTION,
  recentAfterPick,
  recentOf,
  recentStorageKey,
  storeCdsValue,
  storeLabelOf,
  storeNamesOf,
  storeRowLabel,
} from '@/utils/scopeTree'
import { firstStoreOf } from '@/utils/storeOptions'
import { STORE_PICKER_TEXT } from '@/utils/scopeTreeText'

/**
 * **매장 선택 필드** — 종전 매장 셀렉트가 있던 자리에 그대로 선다.
 *
 * 계약은 셀렉트와 **같다**: `v-model` 은 숫자 `storeCd` 하나이고 `change` 도 그 값을 준다(§1-6
 * 「부모 화면의 조회 로직을 건드리지 않는다」). 그래서 부모는 창이 열렸는지, 폴백 셀렉트가 그려졌는지,
 * 매장이 하나뿐이라 고정됐는지를 몰라도 된다.
 *
 * `options` 는 종전 셀렉트의 후보(`GET /store/combo`)를 그대로 받는다 — 두 가지 일을 한다:
 * 폴백 셀렉트의 항목이 되고, 고른 `storeCd` 의 **이름**을 푼다.
 */
export default {
  name: 'StorePicker',

  components: { StorePickerDialog },

  props: {
    /**
     * 고른 매장 — 단일 모드는 숫자 `storeCd`(셀렉트와 같은 값), **다중 모드는 지금 통계 화면이 보내던
     * 그대로의 문자열**(`'11, 12'`)이다. 어느 쪽이든 부모의 조회 로직은 그대로다.
     */
    value: { type: [Number, String], default: null },
    /**
     * 종전 셀렉트의 후보 목록(`storeOptions` 의 행) — 단일 모드의 폴백·이름 풀이에 쓴다.
     *
     * **부모는 빈 배열로 시작해 조회 뒤 채운다**는 전제가 있다(열네 자리 전부 그렇다). 이 prop 의
     * 워처가 immediate 라, 처음부터 채워진 목록을 넘기면 **자식 초기화 중에 부모 모델을 바꾸는**
     * 모양이 된다 — 그런 부모가 생기면 그 자리는 value 를 함께 정해 넘겨야 한다.
     */
    options: { type: Array, default: () => [] },
    /** 다중 선택(통계 화면) — 값이 **선택 규칙**이고 확정 때 서버가 매장 집합으로 푼다(§1-5) */
    multiple: { type: Boolean, default: false },
    /** 비울 수 있는가 — 「전체」가 뜻을 갖는 자리(AI 챗봇)에서만 켠다 */
    clearable: { type: Boolean, default: false },
    /**
     * 빈 값을 무엇이라 부를 것인가 — **비어 있음의 뜻은 부모가 갖는다**(AI 챗봇의 빈 값은 「전체」다).
     * 창 갈래·폴백 셀렉트·고정 이름이 **같은 이 값**을 쓴다: 갈래마다 문구가 갈리면 같은 상태를 화면이
     * 두 가지로 말한다(옛 서버 구간에서 「매장을 고르세요」라고 쓰인 칸이 실제로는 「전체」였다).
     */
    placeholder: { type: String, default: '' },
    /**
     * 트리를 모르는 서버에서 **무엇으로 떨어지는가** — 후보 목록이 있는 자리는 종전 셀렉트('select'),
     * 후보가 없는 자리(정산 백필)는 종전처럼 매장 코드 입력('input')이다.
     *
     * 'input' 인 자리는 **화면 진입에 한 번 물어본다**: 버튼으로 그릴지 입력 칸으로 그릴지가 갈리기
     * 때문이다. 셀렉트 자리는 창을 열 때까지 묻지 않는다(열두 자리에서 요청이 늘지 않게).
     */
    fallback: { type: String, default: 'select' },
    /**
     * 후보가 **여럿**일 때 첫 매장을 기본값으로 세우는가 — 종전 화면들이 `initStore()` 에서 하던 일이다.
     * 그 복제를 여기로 모았으므로 원하지 않는 자리(매장 계정 발급 다이얼로그 — 종전에도 비어 있었다)는
     * 이 prop 으로 끈다. **후보가 하나뿐인 경우는 이것과 무관하다**: 고를 다른 것이 없으므로 언제나 값이다.
     */
    autoSelect: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: 'small' },
  },

  data() {
    return {
      text: STORE_PICKER_TEXT,
      dialogVisible: false,
      /**
       * 서버가 트리를 모르면(404·403) 종전 셀렉트로 떨어진다 — 창이 한 번 답을 주면 그 뒤로는 묻지 않는다.
       * **트리 액션이 없는 세션은 처음부터 떨어져 있다**([SCOPE_TREE_ACTION]) — 그 세션이 창을 열면 보안 계층의
       * 403 이 세션을 끊는다(관리점·운영사가 매장 칸을 누르는 순간 로그아웃이었다)
       */
      unavailable: !canRead({ action: SCOPE_TREE_ACTION }),
      /**
       * 창에서 **배운 이름** — `{storeCd: storeNm}` 라 중복이 구조적으로 없고, 그래서 상한이 없다.
       *
       * 종전에는 고른 행을 배열로 쌓았는데 최근 목록과 같은 규칙·같은 상한이라 **최근 목록의 사본**이
       * 됐고(파생 값을 따로 저장한 것), 상한 5 는 이름의 수명까지 좁혔다: 후보 목록이 빈 계정이 여섯
       * 곳 넘게 고른 뒤 부모가 값을 앞서 고른 매장으로 되돌리면 칸에 **매장 코드**가 떴다.
       */
      learnedNames: {},
      /** 후보가 여럿일 때의 기본값은 **한 번만** 세운다 — 그 뒤 부모가 비우면 비어 있는 것이 답이다 */
      seeded: false,
      recent: [],
      /** 다중 모드의 트리거 요약 — 확정 때 창이 준 칩 문구와 매장 수 */
      chips: [],
      count: 0,
    }
  },

  computed: {
    ...mapGetters({ accountId: 'auth/accountId' }),

    /** 빈 값의 이름 — 부모가 주지 않으면 이 창의 기본 문구다 */
    placeholderText() {
      return this.placeholder || this.text.placeholder
    },

    /**
     * 고를 것이 하나뿐인가 — 이름만 보인다.
     *
     * **다중 모드에는 없는 갈래다**(규칙을 고르는 화면이라 후보 목록을 받지 않는다). **비울 수 있는
     * 자리도 아니다**: 「전체」가 뜻을 갖는 칸(AI 챗봇)은 후보가 하나여도 그 하나와 「전체」 사이를
     * 오갈 수 있어야 하므로 창으로 연다.
     */
    fixed() {
      return !this.multiple && !this.clearable && this.options.length === 1
    },

    /**
     * 이름 사전 — **이번 세션에 본 매장**이다. 최근 목록(계정별 `sessionStorage`, 상한 5)과 창에서
     * 배운 이름이 재료이고, 만드는 규칙은 다중 모드의 색인과 같은 한 함수([storeNamesOf])다.
     */
    names() {
      return Object.assign({}, storeNamesOf(this.recent), this.learnedNames)
    },

    /** 칸에 보이는 이름 — 푸는 규칙은 [storeLabelOf] 한 곳이다(값은 언제나 `value` 하나) */
    label() {
      return this.multiple ? this.summary : storeLabelOf(this.value, this.options, this.names)
    },

    /**
     * 다중 모드의 트리거 칸 — 「피버트 전체 737 외 1건 · 516곳」.
     * 규칙을 그대로 읽어 주는 칩의 **첫 줄**이라 무엇을 고른 창인지 열지 않고도 안다.
     */
    summary() {
      if (this.chips.length === 0) return ''
      const head = this.chips[0]
      const rest = this.chips.length - 1
      const more = rest > 0 ? ` ${this.text.summaryMore} ${rest}${this.text.summaryMoreUnit}` : ''

      return `${head}${more} · ${this.count}${this.text.storeCountUnit}`
    },
  },

  watch: {
    /**
     * 후보가 정해지는 순간이 **기본값이 정해지는 순간**이다 — 종전에는 화면 여섯 곳이 각자
     * `initStore()` 에서 하던 일이고, 그 자리가 없는 화면(매장 계정 발급)에서는 값이 영영 `null` 이라
     * 저장이 400 이었다. 후보를 아는 곳이 여기뿐이므로 여기가 소유자다.
     */
    options: { immediate: true, handler: 'seedDefault' },
    value: 'seedDefault',
  },

  created() {
    this.recent = this.readRecent()
    // 처음부터 떨어진 선택기도 부모에게 알린다 — 통계 화면은 그 신호로 옛 체크박스 트리를 그린다
    if (this.unavailable) this.$emit('unavailable')
  },

  mounted() {
    // 폴백이 입력 칸인 자리는 **미리** 물어본다 — 버튼인지 입력 칸인지가 그 답으로 갈린다
    if (!this.multiple && this.fallback === 'input' && this.$refs.dialog) this.$refs.dialog.loadRoots()
  },

  methods: {
    /**
     * 「고를 것이 정해졌다」를 **값으로** 만든다. 두 규칙이고 뜻이 다르다:
     *
     * - **후보가 하나** — 그것이 곧 값이다. 비면 다시 채운다(고를 다른 것이 없으므로 빈 값은 뜻이 아니라
     *   사고다 — 매장 계정 발급 화면이 그렇게 저장 400 을 맞았다).
     * - **후보가 여럿** — 첫 매장을 **한 번만** 세운다(종전 `initStore()` 의 기본). 그 뒤 부모가 비우면
     *   비어 있는 것이 답이다. 이 기본을 원하지 않는 자리는 `auto-select="false"` 로 끈다.
     *
     * 「전체」가 뜻을 갖는 칸(`clearable`)과 규칙을 고르는 다중 모드에는 기본값이 없다.
     */
    seedDefault() {
      if (this.multiple || this.clearable) return

      const first = firstStoreOf(this.options)
      if (first == null) return

      const only = this.options.length === 1
      if (!only) {
        if (this.seeded || !this.autoSelect) return
        this.seeded = true
      }
      if (this.value != null && this.value !== '') return

      this.emitPick(first.storeCd)
    },

    open() {
      this.dialogVisible = true
    },

    /** 비우기 — 「고르지 않음」도 값이다(그 뜻은 부모가 갖는다: AI 챗봇은 「전체」) */
    clear() {
      this.emitPick(null)
    },

    onPick(row) {
      // 창만이 아는 이름을 사전에 남긴다 — 값은 `storeCd` 하나뿐이고 이름은 그 값으로 푼다
      this.learnedNames = Object.assign({}, this.learnedNames, storeNamesOf([row]))
      this.recent = recentAfterPick(this.recent, row)
      this.writeRecent(this.recent)
      this.emitPick(row.storeCd)
    },

    /**
     * 다중 모드의 확정 — 창이 **서버에게 규칙을 풀게 해서** 받은 매장 집합을 지금과 같은 모양으로 준다
     * (`', '` join). 통계 화면의 조회 로직은 이 값만 본다.
     */
    onConfirm(result) {
      this.chips = (result.chips || []).map((chip) => chip.label)
      this.count = result.total || 0
      this.emitPick(storeCdsValue(result.storeCds))
    },

    /** 서버가 트리를 모르면 부모에게도 알린다 — 통계 화면은 **옛 체크박스 트리**로 떨어진다 */
    onUnavailable() {
      this.unavailable = true
      this.$emit('unavailable')
    },

    /** 폴백 셀렉트 항목의 이름 — 창 갈래와 **같은 해석기**다([storeRowLabel]) */
    rowLabel: storeRowLabel,

    /** 셀렉트와 **같은 두 이벤트** — `v-model` 과 `change` 다 */
    emitPick(storeCd) {
      this.$emit('input', storeCd)
      this.$emit('change', storeCd)
    },

    // ── 저장 경계(계정마다 따로, 그 탭 안에서만) ──────────────────────────

    readRecent() {
      const key = recentStorageKey(this.accountId)
      if (!key) return []

      try {
        return recentOf(window.sessionStorage.getItem(key))
      } catch (error) {
        // 저장소를 못 읽는 브라우저(시크릿·차단)에서도 창은 그대로 열린다
        return []
      }
    },

    writeRecent(rows) {
      const key = recentStorageKey(this.accountId)
      if (!key) return

      try {
        window.sessionStorage.setItem(key, JSON.stringify(rows))
      } catch (error) {
        // 남기지 못해도 고르는 일은 끝났다
      }
    },
  },
}
</script>

<style scoped>
.store-picker--fixed {
  font-size: 13px;
  line-height: 32px;
}
.store-picker__field {
  text-align: left;
  width: 100%;
}
.store-picker__placeholder {
  color: #c0c4cc;
}
.store-picker__icon {
  color: #c0c4cc;
  float: right;
  line-height: inherit;
}
</style>
